import type React from "react";

export const HALL_AD_WIDTH = 360;
export const HALL_AD_HEIGHT = 540;
export const HALL_AD_FONTS = ["Arimo", "Poppins", "Tinos", "Cousine", "Anton"];

const FONT_ALIASES: Record<string, string> = {
  arial: "Arimo",
  arimo: "Arimo",
  "lucida sans unicode": "Arimo",
  "trebuchet ms": "Arimo",
  verdana: "Arimo",
  poppins: "Poppins",
  georgia: "Tinos",
  "palatino linotype": "Tinos",
  "times new roman": "Tinos",
  tinos: "Tinos",
  "courier new": "Cousine",
  "lucida console": "Cousine",
  cousine: "Cousine",
  impact: "Anton",
  anton: "Anton",
};

// The first family is canonical for updated readers; older installed Apps can
// still resolve the following legacy system family before falling back generically.
const FONT_STACKS: Record<string, string> = {
  Arimo: "Arimo, Arial, sans-serif",
  Poppins: "Poppins, Arial, sans-serif",
  Tinos: 'Tinos, "Times New Roman", serif',
  Cousine: 'Cousine, "Courier New", monospace',
  Anton: "Anton, Impact, sans-serif",
};

export function hallAdFont(value: unknown): string {
  const first = String(value ?? "")
    .split(",")[0]
    .trim()
    .replace(/^['"]|['"]$/g, "")
    .toLowerCase()
    .replace(/^dakasa ad /, "");
  return FONT_ALIASES[first] ?? "Arimo";
}

export interface CanvasAdPart {
  id: string;
  type: "button" | "text" | "image" | "background";
  css: React.CSSProperties;
  x: number;
  y: number;
  rotation: number;
  zIndex: number;
  text?: string;
  src?: string;
  link?: string;
}

function dimensionPixels(value: unknown, base: number): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? (value * base) / 100 : undefined;
  }
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^(\d+(?:\.\d+)?|\.\d+)(px|%)$/i);
  if (!match || Number(match[1]) <= 0) return undefined;
  return match[2] === "%" ? (Number(match[1]) * base) / 100 : Number(match[1]);
}

function unsupportedStyle(property: string, value: unknown): boolean {
  if (property === "fontFamily") return false;
  if (property === "font" || property.startsWith("--")) return true;
  if (property === "all" && value !== "initial") return true;
  if (
    /^(?:zoom|scale|translate|rotate|perspective|perspectiveOrigin|transformStyle|inlineSize|blockSize|textSizeAdjust|WebkitTextSizeAdjust)$/.test(
      property,
    )
  )
    return true;
  if (property === "fontSynthesis" && value !== "none") return true;
  if (property === "fontSize") {
    if (typeof value === "number") return !Number.isFinite(value) || value < 0;
    return typeof value !== "string" || !/^(?:\d+(?:\.\d+)?|\.\d+)(?:px|%)$/i.test(value.trim());
  }
  if (property === "boxSizing" && value !== "border-box") return true;
  if (/^(?:min|max)(?:Width|Height|BlockSize|InlineSize)$/.test(property)) return true;
  if (
    property === "display" &&
    !["block", "flex", "inline-block", "inline-flex", "grid", "inline-grid"].includes(String(value))
  )
    return true;
  if (/^margin/.test(property) && value !== 0 && value !== "0" && value !== "0px") return true;
  if (property === "transform" && typeof value === "string" && /rotate\s*\(/i.test(value))
    return true;
  if (
    /^(?:position|inset(?:Block|Inline)?(?:Start|End)?|top|right|bottom|left|zIndex|float|clear|animation.*|transition.*)$/.test(
      property,
    )
  )
    return true;
  if (property === "backgroundAttachment" && value !== "scroll") return true;
  if (typeof value !== "string") return false;
  if (/\b(?:var|env|calc|min|max|clamp)\s*\(/i.test(value)) return true;
  if (/^\s*(?:inherit|unset|revert(?:-layer)?)\s*$/i.test(value)) return true;
  if (
    /[\d.](?:rem|em|ex|ch|cap|ic|lh|rlh|vw|vh|vi|vb|svw|svh|lvw|lvh|dvw|dvh|vmin|vmax|cqw|cqh|cqi|cqb|cqmin|cqmax)\b/i.test(
      value,
    )
  ) {
    return true;
  }
  return /^(?:padding|margin|textIndent)/.test(property) && value.includes("%");
}

/** Publication rejects host-dependent CSS instead of saving a misleading preview. */
export function hallAdStyleError(css: React.CSSProperties): string | undefined {
  for (const [property, value] of Object.entries(css)) {
    if (unsupportedStyle(property, value)) {
      return `O estilo ${property} não é compatível com a prévia fixa do anúncio. Use tamanhos em px e os controles de posição.`;
    }
  }
  for (const [property, base] of [
    ["width", HALL_AD_WIDTH],
    ["height", HALL_AD_HEIGHT],
  ] as const) {
    if (css[property] !== undefined && dimensionPixels(css[property], base) === undefined) {
      return `Defina ${property} com um tamanho positivo em px ou % no anúncio.`;
    }
  }
  return undefined;
}

/** Same defaults and legacy aliases in the editor, saved preview and App canvas. */
export function hallAdStyle(
  type: CanvasAdPart["type"],
  css: React.CSSProperties = {},
): React.CSSProperties {
  const contentCSS = { ...css };
  if (typeof contentCSS.transform === "string") {
    contentCSS.transform =
      contentCSS.transform.replace(/rotate\s*\([^)]*\)/gi, "").trim() || "none";
  }
  const fixed = Object.fromEntries(
    Object.entries(contentCSS).filter(
      ([property, value]) => property !== "all" && !unsupportedStyle(property, value),
    ),
  ) as React.CSSProperties;
  return {
    all: "initial",
    boxSizing: "border-box",
    display: type === "button" ? "flex" : "block",
    alignItems: "center",
    justifyContent: "center",
    appearance: "none",
    margin: 0,
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "#000000",
    fontSize: "14px",
    fontWeight: 400,
    fontStyle: "normal",
    fontSynthesis: "none",
    lineHeight: 1.2,
    letterSpacing: "0px",
    wordSpacing: "0px",
    whiteSpace: "pre-wrap",
    overflowWrap: "normal",
    wordBreak: "normal",
    textAlign: "center",
    textTransform: "none",
    textDecoration: "none",
    ...fixed,
    fontFamily: `"DaKasa Ad ${hallAdFont(css.fontFamily)}"`,
    width: `${dimensionPixels(css.width, HALL_AD_WIDTH) ?? (type === "button" ? 200 : 100)}px`,
    height: `${dimensionPixels(css.height, HALL_AD_HEIGHT) ?? 40}px`,
  };
}

const BACKGROUND_PROPERTIES = new Set([
  "background",
  "backgroundColor",
  "backgroundImage",
  "borderImageSource",
  "maskImage",
  "clipPath",
  "opacity",
  "mixBlendMode",
  "isolation",
  "filter",
  "backgroundRepeat",
  "backgroundAttachment",
  "backgroundPosition",
  "backgroundSize",
  "maskRepeat",
  "maskPosition",
  "maskSize",
  "imageRendering",
]);

export function hallAdBackground(css: React.CSSProperties = {}): React.CSSProperties {
  return {
    backgroundColor: "#ffffff",
    ...Object.fromEntries(
      Object.entries(css).filter(
        ([property, value]) =>
          BACKGROUND_PROPERTIES.has(property) && !unsupportedStyle(property, value),
      ),
    ),
  };
}

export function serializeHallAdPart<T extends CanvasAdPart>(part: T): T {
  const css = hallAdStyle(part.type, part.css);
  return {
    ...part,
    css: {
      ...css,
      fontFamily: FONT_STACKS[hallAdFont(part.css.fontFamily)],
      width: (parseFloat(String(css.width)) / HALL_AD_WIDTH) * 100,
      height: (parseFloat(String(css.height)) / HALL_AD_HEIGHT) * 100,
    },
  };
}

export function toCanvasAdPart<T extends CanvasAdPart>(part: T): T {
  const css = hallAdStyle(part.type, part.css);
  const cleanTransform = String(css.transform ?? "")
    .replace(/^none$/i, "")
    .trim();
  return {
    ...part,
    css: { ...css, transform: `rotate(${part.rotation ?? 0}deg) ${cleanTransform}`.trim() },
  };
}

/** Wait for the exact bundled font faces before publishing fixed text geometry. */
export async function ensureHallAdFonts(parts: CanvasAdPart[]): Promise<void> {
  if (typeof document === "undefined") return;
  if (!document.fonts)
    throw new Error(
      "Não foi possível verificar as fontes. Atualize o navegador antes de publicar.",
    );
  const samples = new Map<string, string>();
  for (const part of parts) {
    if (part.type !== "text" && part.type !== "button") continue;
    const css = hallAdStyle(part.type, part.css);
    const spec = `${css.fontStyle} ${css.fontWeight} 14px ${css.fontFamily}`;
    samples.set(spec, `${samples.get(spec) ?? "Anúncio Áéç Ãõ"} ${part.text ?? ""}`);
  }
  for (const [spec, sample] of samples) {
    const loaded = await document.fonts.load(spec, sample);
    if (loaded.length === 0 || !document.fonts.check(spec, sample)) {
      throw new Error(
        "Não foi possível carregar a fonte do anúncio. Tente novamente antes de publicar.",
      );
    }
  }
}
